import React, { useState, useEffect, useRef } from 'react';
import { Cpu, Loader, Activity } from 'lucide-react';
import axiosClient from '../../api/axiosClient';

const TERMINAL_STATUSES = ['completed', 'stopped', 'failed', 'cancelled'];
const SEV_STYLE = {
    critical: 'bg-red-950/40 text-red-400 border-red-500/30',
    high: 'bg-orange-950/40 text-orange-400 border-orange-500/30',
    medium: 'bg-amber-950/40 text-amber-400 border-amber-500/30',
    low: 'bg-blue-950/40 text-blue-400 border-blue-500/30',
    info: 'bg-slate-800/40 text-slate-400 border-slate-700/30',
};
const sevClass = (s) => SEV_STYLE[(s || '').toLowerCase()] || SEV_STYLE.info;

const MicroAgentsPanel = ({ scanId, compact = false, scanStatus = 'running' }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (!scanId) return;

        let stopped = false;

        const fetchData = async () => {
            try {
                const res = await axiosClient.get(`/admin/web-audit/micro-agents/${scanId}`);
                if (!stopped) {
                    setData(res);
                    setError(null);
                }
            } catch (e) {
                if (!stopped) setError(e.response?.data?.detail || e.message);
            } finally {
                if (!stopped) setLoading(false);
            }
        };

        fetchData();

        const isTerminal = TERMINAL_STATUSES.includes(scanStatus);
        if (!isTerminal) {
            intervalRef.current = setInterval(fetchData, 3000);
        }

        return () => {
            stopped = true;
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [scanId, scanStatus]);

    // stop polling once server reports terminal state
    useEffect(() => {
        if (data && TERMINAL_STATUSES.includes(data.scan_status) && intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, [data]);

    if (loading) {
        return (
            <div className="text-center py-4 text-xs text-slate-500">
                <Loader className="animate-spin inline mr-1" size={14} /> Loading agent telemetry...
            </div>
        );
    }
    if (error) {
        return (
            <div className="text-center py-4 text-xs text-red-400">
                Failed to load micro agents: {error}
            </div>
        );
    }
    if (!data) return null;

    const { summary, agents, recent_tasks, findings = [], scan_status } = data;
    const isTerminal = TERMINAL_STATUSES.includes(scan_status);

    // ─────────────────────────────────────────────
    // COMPACT MODE — for live scanning log modal
    // ─────────────────────────────────────────────
    if (compact) {
        if (summary.agents_total === 0) {
            return (
                <div className="border-b border-slate-900 bg-slate-950/50 px-4 py-2 text-[10px] text-slate-500 font-mono">
                    <Cpu size={11} className="inline mr-1.5 text-cyan-600" />
                    Micro agent swarm not yet spawned…
                </div>
            );
        }
        const runningAgents = agents.filter(a => a.state === 'running');
        return (
            <div className="border-b border-slate-900 bg-slate-950/60 px-4 py-3">
                <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                    <span className="text-[9px] font-bold text-cyan-400 uppercase tracking-widest flex items-center gap-1.5">
                        <Activity size={11} className="text-cyan-500 animate-pulse" /> MICRO AGENT SWARM
                    </span>
                    <div className="flex items-center gap-3 text-[9px] font-mono">
                        <span className="text-emerald-400">● {summary.agents_active} active</span>
                        <span className="text-amber-400">● {summary.agents_idle} idle</span>
                        <span className="text-slate-500">● {summary.agents_terminated} done</span>
                    </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 text-[9px] font-mono">
                    <div className="bg-black/50 border border-slate-900 rounded px-2 py-1.5">
                        <div className="text-slate-600 uppercase tracking-wider">Agents</div>
                        <div className="text-cyan-400 font-bold">{summary.agents_total}</div>
                    </div>
                    <div className="bg-black/50 border border-slate-900 rounded px-2 py-1.5">
                        <div className="text-slate-600 uppercase tracking-wider">Tasks</div>
                        <div className="text-cyan-400 font-bold">{summary.tasks_total}</div>
                    </div>
                    <div className="bg-black/50 border border-slate-900 rounded px-2 py-1.5">
                        <div className="text-slate-600 uppercase tracking-wider">Findings</div>
                        <div className="text-cyan-400 font-bold">{summary.findings_total}</div>
                    </div>
                    <div className="bg-black/50 border border-slate-900 rounded px-2 py-1.5">
                        <div className="text-slate-600 uppercase tracking-wider">Status</div>
                        <div className={`font-bold uppercase ${isTerminal ? 'text-slate-500' : 'text-emerald-400'}`}>
                            {isTerminal ? 'done' : 'live'}
                        </div>
                    </div>
                </div>
                {runningAgents.length > 0 && (
                    <div className="mt-2 space-y-0.5">
                        {runningAgents.slice(0, 4).map(a => (
                            <div
                                key={a.agent_id}
                                className="text-[9px] text-slate-500 font-mono truncate"
                                title={`${a.current_task?.module || ''} → ${a.current_task?.target_url || ''}`}
                            >
                                <span className="text-emerald-500">▶</span>{' '}
                                <span className="text-cyan-500">{a.agent_id}</span>:{' '}
                                {a.current_task?.module || 'idle'} → {a.current_task?.target_url || ''}
                            </div>
                        ))}
                        {runningAgents.length > 4 && (
                            <div className="text-[9px] text-slate-600 font-mono">
                                … +{runningAgents.length - 4} more active
                            </div>
                        )}
                    </div>
                )}
                {findings.length > 0 && (
                    <div className="mt-2 border-t border-slate-900 pt-2">
                        <div className="text-[9px] font-bold text-cyan-400 uppercase tracking-widest mb-1 flex items-center justify-between">
                            <span>🎯 Latest Findings ({findings.length})</span>
                        </div>
                        <div className="space-y-0.5 max-h-32 overflow-y-auto custom-scrollbar pr-1">
                            {findings.slice(0, 5).map((f) => (
                                <div
                                    key={f.id}
                                    className="text-[9px] font-mono truncate flex gap-1.5 items-center"
                                    title={f.title}
                                >
                                    <span className={`px-1 py-0.5 rounded text-[8px] font-bold uppercase border ${sevClass(f.severity)}`}>
                                        {f.severity}
                                    </span>
                                    <span className="text-slate-400 truncate">{f.title}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ─────────────────────────────────────────────
    // FULL MODE — for details modal tab
    // ─────────────────────────────────────────────
    if (summary.agents_total === 0) {
        return (
            <div className="text-center text-slate-600 py-12 text-xs italic">
                No micro agents were spawned for this scan. Micro Agent mode may not have been enabled.
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Summary bar */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <div className="bg-slate-950/40 border border-slate-900 p-3 rounded-lg text-center">
                    <div className="text-lg font-bold text-cyan-400">{summary.agents_total}</div>
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">Agents</div>
                </div>
                <div className="bg-emerald-950/20 border border-emerald-500/20 p-3 rounded-lg text-center">
                    <div className="text-lg font-bold text-emerald-400">{summary.agents_active}</div>
                    <div className="text-[9px] text-emerald-400 uppercase tracking-wider">Active</div>
                </div>
                <div className="bg-amber-950/20 border border-amber-500/20 p-3 rounded-lg text-center">
                    <div className="text-lg font-bold text-amber-400">{summary.agents_idle}</div>
                    <div className="text-[9px] text-amber-400 uppercase tracking-wider">Idle</div>
                </div>
                <div className="bg-slate-800/40 border border-slate-700/30 p-3 rounded-lg text-center">
                    <div className="text-lg font-bold text-slate-400">{summary.agents_terminated}</div>
                    <div className="text-[9px] text-slate-500 uppercase tracking-wider">Terminated</div>
                </div>
                <div className="bg-cyan-950/20 border border-cyan-500/20 p-3 rounded-lg text-center">
                    <div className="text-lg font-bold text-cyan-400">{summary.findings_total}</div>
                    <div className="text-[9px] text-cyan-400 uppercase tracking-wider">Findings</div>
                </div>
            </div>

            {/* Agents roster */}
            <div className="bg-slate-950/40 border border-slate-900 rounded-xl p-4">
                <h4 className="text-[10px] font-bold text-cyan-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <Cpu size={12} className="text-cyan-600" /> Agent Roster ({agents.length})
                </h4>
                <div className="space-y-2 max-h-72 overflow-y-auto custom-scrollbar pr-1">
                    {agents.map(a => {
                        const stateColor = {
                            running: 'bg-emerald-950/20 text-emerald-400 border-emerald-500/30',
                            idle: 'bg-amber-950/20 text-amber-400 border-amber-500/30',
                            terminated: 'bg-slate-800/40 text-slate-500 border-slate-700/30',
                        }[a.state] || 'bg-slate-800/40 text-slate-500 border-slate-700/30';

                        return (
                            <div
                                key={a.agent_id}
                                className="bg-black/40 border border-slate-900/60 p-3 rounded-lg hover:border-slate-800 transition-colors"
                            >
                                <div className="flex justify-between items-start gap-2 flex-wrap">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-mono text-cyan-400 font-bold">{a.agent_id}</span>
                                        <span className={`text-[8px] px-2 py-0.5 rounded font-bold uppercase border tracking-widest ${stateColor}`}>
                                            {a.state}
                                        </span>
                                    </div>
                                    <div className="text-[10px] text-slate-500 font-mono">
                                        tasks: <span className="text-slate-300">{a.tasks_completed}</span> · findings:{' '}
                                        <span className="text-slate-300">{a.findings_produced}</span>
                                    </div>
                                </div>
                                {a.current_task && (
                                    <div className="mt-2 text-[10px] text-slate-400 font-mono truncate" title={a.current_task.target_url}>
                                        <span className="text-emerald-500">▶</span>{' '}
                                        <span className="text-cyan-500">{a.current_task.module}</span> → {a.current_task.target_url}
                                    </div>
                                )}
                                {a.last_seen && (
                                    <div className="text-[9px] text-slate-600 mt-1">
                                        last activity: {new Date(a.last_seen).toLocaleTimeString()}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Recent tasks table */}
            <div className="bg-slate-950/40 border border-slate-900 rounded-xl p-4">
                <h4 className="text-[10px] font-bold text-cyan-400 uppercase tracking-widest mb-3">
                    Recent Task Ledger ({recent_tasks.length})
                </h4>
                <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-left text-[10px]">
                        <thead className="text-slate-500 uppercase tracking-widest border-b border-slate-800">
                            <tr>
                                <th className="p-2">Agent</th>
                                <th className="p-2">Module</th>
                                <th className="p-2">Target</th>
                                <th className="p-2">Status</th>
                                <th className="p-2 text-center">Findings</th>
                                <th className="p-2">Error</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recent_tasks.map(t => (
                                <tr key={t.task_id} className="border-b border-slate-900/50 hover:bg-slate-900/20">
                                    <td className="p-2 text-cyan-400 font-mono whitespace-nowrap">{t.agent_id}</td>
                                    <td className="p-2 text-slate-300 whitespace-nowrap">{t.module_name}</td>
                                    <td className="p-2 text-slate-500 truncate max-w-xs" title={t.target_url}>
                                        {t.target_url}
                                    </td>
                                    <td className="p-2">
                                        <span className={`px-1.5 py-0.5 rounded text-[8px] font-bold uppercase ${t.status === 'ok' ? 'bg-emerald-950/20 text-emerald-400' :
                                                t.status === 'timeout' ? 'bg-amber-950/20 text-amber-400' :
                                                    'bg-red-950/20 text-red-400'
                                            }`}>
                                            {t.status}
                                        </span>
                                    </td>
                                    <td className="p-2 text-slate-400 text-center">{t.findings_count}</td>
                                    <td
                                        className="p-2 text-red-400 max-w-xs truncate"
                                        title={t.error_message || ''}
                                    >
                                        {t.error_message || '—'}
                                    </td>
                                </tr>
                            ))}
                            {recent_tasks.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="text-center text-slate-600 py-4 italic">
                                        No tasks recorded yet.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* C4 — Live Findings Feed */}
            <div className="bg-slate-950/40 border border-slate-900 rounded-xl p-4">
                <h4 className="text-[10px] font-bold text-cyan-400 uppercase tracking-widest mb-3 flex items-center justify-between">
                    <span>🎯 Live Findings ({findings.length})</span>
                    {!isTerminal && (
                        <span className="text-[9px] text-emerald-400 animate-pulse">
                            ● streaming
                        </span>
                    )}
                </h4>
                <div className="space-y-2 max-h-80 overflow-y-auto custom-scrollbar pr-1">
                    {findings.map((f) => (
                        <div
                            key={f.id}
                            className="bg-black/40 border border-slate-900/60 rounded-lg p-3 hover:border-slate-800 transition-colors"
                        >
                            <div className="flex justify-between items-start gap-2 mb-1">
                                <span className="text-xs text-slate-200 font-bold break-words">
                                    {f.title}
                                </span>
                                <span className={`text-[8px] px-2 py-0.5 rounded font-bold uppercase border ${sevClass(f.severity)} whitespace-nowrap shrink-0`}>
                                    {f.severity}
                                </span>
                            </div>
                            <div className="flex gap-3 text-[9px] text-slate-500 font-mono">
                                {f.category && (
                                    <span className="bg-slate-900/60 px-1.5 py-0.5 rounded">
                                        {f.category}
                                    </span>
                                )}
                                {f.ai_confidence != null && (
                                    <span>conf: {Math.round(f.ai_confidence)}%</span>
                                )}
                            </div>
                            {f.fix_snippet && (
                                <div className="mt-2 text-[10px] text-emerald-500/80 font-mono leading-relaxed">
                                    <span className="text-slate-600 font-bold uppercase text-[8px] mr-1">fix:</span>
                                    {f.fix_snippet}
                                </div>
                            )}
                            {f.provenance && (
                                <details className="mt-2">
                                    <summary className="text-[9px] text-cyan-500 cursor-pointer uppercase tracking-wider font-bold">
                                        How found
                                    </summary>
                                    <div className="mt-1 ml-2 text-[9px] text-slate-500 font-mono space-y-0.5">
                                        <div>agent: <span className="text-cyan-400">{f.provenance.agent_id}</span></div>
                                        <div>tool: <span className="text-cyan-400">{f.provenance.tool}</span></div>
                                        {f.provenance.endpoint && (
                                            <div className="truncate">endpoint: <span className="text-slate-300">{f.provenance.endpoint}</span></div>
                                        )}
                                        {f.provenance.payload && (
                                            <div className="truncate">payload: <span className="text-slate-300">{f.provenance.payload}</span></div>
                                        )}
                                        {f.provenance.response_status && (
                                            <div>status: <span className="text-emerald-400">{f.provenance.response_status}</span></div>
                                        )}
                                    </div>
                                </details>
                            )}
                        </div>
                    ))}
                    {findings.length === 0 && (
                        <div className="text-center text-slate-600 py-6 italic text-xs">
                            No findings yet. Waiting for agents to report…
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
};

export default MicroAgentsPanel;